import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { FINANCE_TABS } from "../components/SectionTabs";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, TrendingUp, Clock, CheckCircle2, AlertTriangle, FileText, Search, ChevronRight, DollarSign, ArrowUpRight, ArrowDownRight, Loader2, CreditCard, Banknote, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0));

const STATUS_LABELS = {
  editee: "Éditée", deposee: "Déposée", rejetee: "Rejetée",
  paiement_partiel: "Paiement partiel", paiement_total: "Paiement total",
  paiement_effectue: "Payé", archived: "Archivé"
};
const STATUS_COLORS = {
  editee: "bg-slate-200 text-slate-700", deposee: "bg-sky-100 text-sky-700",
  rejetee: "bg-red-100 text-red-700", paiement_partiel: "bg-amber-100 text-amber-700",
  paiement_total: "bg-teal-100 text-teal-700", paiement_effectue: "bg-emerald-100 text-emerald-700",
  archived: "bg-gray-200 text-gray-600"
};

export default function LogPaymentTrackingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterInsurer, setFilterInsurer] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/invoices/payment-tracking`, { headers: getHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error("Erreur de chargement des donnees de paiement");
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/log/invoices/payment-tracking/export-pdf`, {
        headers: getHeaders(), responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `CMES_Suivi_Paiements_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF telecharge avec succes");
    } catch (err) {
      toast.error("Erreur de telechargement du PDF");
    } finally { setDownloading(false); }
  };

  const filteredPartials = (data?.partial_invoices || []).filter(inv => {
    const matchSearch = !searchTerm || inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) || inv.insurer?.toLowerCase().includes(searchTerm.toLowerCase()) || inv.patient?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchInsurer = filterInsurer === "all" || inv.insurer === filterInsurer;
    return matchSearch && matchInsurer;
  });

  const insurerNames = [...new Set((data?.partial_invoices || []).map(i => i.insurer).filter(Boolean))];

  if (loading) return (
    <LogLayout title="Finance — Créances & Reçus">
      <SectionTabs tabs={FINANCE_TABS} />
      <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
    </LogLayout>
  );

  const s = data?.summary || {};

  return (
    <LogLayout title="Finance — Créances & Reçus" subtitle="Reçus : historique des paiements partiels et totaux">
      <SectionTabs tabs={FINANCE_TABS} />
      <div className="space-y-6" data-testid="payment-tracking-page">
        {/* Download bar */}
        <div className="flex justify-end">
          <Button onClick={downloadPdf} disabled={downloading || loading}
            className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="payment-download-pdf">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Telecharger le suivi en PDF
          </Button>
        </div>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Facture", value: `${fmt(s.total_invoiced)} F`, icon: FileText, color: "blue", sub: `${s.total_invoices || 0} factures` },
            { label: "Total Paye", value: `${fmt(s.total_paid)} F`, icon: CheckCircle2, color: "emerald", sub: `${s.total_payments || 0} paiements` },
            { label: "Reste a Payer", value: `${fmt(s.total_remaining)} F`, icon: AlertTriangle, color: "amber", sub: `${s.partial_count || 0} en cours` },
            { label: "Taux Recouvrement", value: `${s.recovery_rate || 0}%`, icon: TrendingUp, color: s.recovery_rate >= 80 ? "emerald" : s.recovery_rate >= 50 ? "amber" : "red", sub: `${s.fully_paid_count || 0} soldees` },
          ].map((kpi, i) => (
            <Card key={i} className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{kpi.label}</p>
                    <p className={`text-xl font-bold mt-1 text-${kpi.color}-600`}>{kpi.value}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{kpi.sub}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl bg-${kpi.color}-50 flex items-center justify-center`}>
                    <kpi.icon className={`w-5 h-5 text-${kpi.color}-500`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoices with partial payments */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-cyan-500" /> Factures avec paiements ({filteredPartials.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        className="pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 text-sm w-40 focus:border-cyan-500" data-testid="payment-search" />
                    </div>
                    <select value={filterInsurer} onChange={e => setFilterInsurer(e.target.value)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="payment-insurer-filter">
                      <option value="all">Tous les assureurs</option>
                      {insurerNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {filteredPartials.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Wallet className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Aucun paiement enregistre</p>
                    </div>
                  ) : filteredPartials.map((inv, i) => (
                    <div key={inv.id}
                      className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/log/invoices`)}
                      data-testid={`partial-invoice-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                            inv.progress >= 100 ? "bg-emerald-100 text-emerald-700" :
                            inv.progress >= 50 ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-700"
                          }`}>{Math.round(inv.progress)}%</div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{inv.invoice_number}</p>
                            <p className="text-[11px] text-slate-400">{inv.insurer} {inv.patient ? `- ${inv.patient}` : ""}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-700">{fmt(inv.amount)} F</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[inv.status] || "bg-slate-100 text-slate-600"}`}>
                            {STATUS_LABELS[inv.status] || inv.status}
                          </span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${
                            inv.progress >= 100 ? "bg-emerald-500" : inv.progress >= 50 ? "bg-amber-500" : "bg-red-500"
                          }`} style={{ width: `${Math.min(inv.progress, 100)}%` }} />
                        </div>
                        <div className="flex gap-3 text-[11px]">
                          <span className="text-emerald-600 font-medium">{fmt(inv.paid)} paye</span>
                          <span className="text-amber-600 font-medium">{fmt(inv.remaining)} restant</span>
                          <span className="text-slate-400">{inv.payment_count} pmt(s)</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* By Insurer */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-blue-500" /> Par assureur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(data?.by_insurer || {}).sort((a, b) => b[1].remaining - a[1].remaining).map(([name, info]) => (
                  <div key={name} className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-slate-700 truncate flex-1">{name}</p>
                      <span className="text-[10px] text-slate-400 ml-2">{info.count} fact.</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-emerald-600">{fmt(info.paid)} paye</span>
                      <span className="text-amber-600 font-medium">{fmt(info.remaining)} restant</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${Math.min((info.paid / Math.max(info.amount, 1)) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
                {Object.keys(data?.by_insurer || {}).length === 0 && <p className="text-xs text-slate-400 text-center py-4">Aucun assureur</p>}
              </CardContent>
            </Card>

            {/* Recent Payments */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-violet-500" /> Derniers paiements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 max-h-80 overflow-y-auto">
                {(data?.payments || []).slice(0, 15).map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.method === "especes" ? "bg-emerald-50" : "bg-blue-50"}`}>
                      {p.method === "especes" ? <Banknote className="w-4 h-4 text-emerald-600" /> : <CreditCard className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{p.invoice_number}</p>
                      <p className="text-[10px] text-slate-400 truncate">{p.insurer} {p.patient ? `- ${p.patient}` : ""}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-bold text-emerald-600">+{fmt(p.amount)} F</p>
                      <p className="text-[9px] text-slate-400">{p.date ? new Date(p.date).toLocaleDateString("fr-FR") : ""}</p>
                    </div>
                  </div>
                ))}
                {(data?.payments || []).length === 0 && <p className="text-xs text-slate-400 text-center py-6">Aucun paiement enregistre</p>}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* By Status summary */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Repartition par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {Object.entries(data?.by_status || {}).map(([status, info]) => (
                <div key={status} className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
                    {STATUS_LABELS[status] || status}
                  </span>
                  <p className="text-lg font-bold text-slate-800 mt-1">{info.count}</p>
                  <p className="text-[10px] text-slate-400">{fmt(info.amount)} F</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </LogLayout>
  );
}
